import { useState, useEffect } from "react";
import { IDashboardFilters } from "../interfaces/dashboard";

interface ISectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

type TFetcher<T> = (filters: IDashboardFilters) => Promise<T>;

export const useDashboardSection = <T>(
  fetcher: TFetcher<T>,
  filters: IDashboardFilters,
  // Cuando es false la sección no se pide (p.ej. Top merchants para entidades
  // que no tienen merchants): queda en estado inerte (sin loading ni datos) y
  // así el consumidor puede no montarla sin gastar un request al BFF.
  enabled = true,
): ISectionState<T> & { retry: () => void } => {
  const [state, setState] = useState<ISectionState<T>>({
    data: null,
    loading: enabled,
    error: null,
  });

  // Contador de reintentos: al incrementarlo cambia una dependencia del efecto
  // y se dispara un nuevo fetch. Debe ser estado (no ref) para forzar el
  // re-render que reevalúa las dependencias.
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // fetchData no propaga el AbortController de axios, así que no podemos
    // cancelar el request en vuelo. Usamos un flag capturado en el closure para
    // ignorar la respuesta de un fetch que quedó obsoleto (evita que una
    // respuesta vieja pise el estado de una más nueva cuando cambian los filtros
    // en rápida sucesión).
    let ignore = false;

    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return () => {
        ignore = true;
      };
    }

    setState({ data: null, loading: true, error: null });

    fetcher(filters)
      .then((data) => {
        if (!ignore) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        // fetchData rechaza con un string (el mensaje del backend), no con un
        // Error. Normalizamos ambos casos para no perder el mensaje real.
        const message =
          typeof err === "string"
            ? err
            : (err as Error)?.message || "Error al cargar datos";
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      ignore = true;
    };
  }, [
    filters.from,
    filters.to,
    filters.carrier_id,
    filters.status,
    filters.province,
    filters.merchant_id,
    filters.client_id,
    filters.marketplaceId,
    filters.country,
    enabled,
    retryCount,
  ]);

  const retry = () => setRetryCount((count) => count + 1);

  return { ...state, retry };
};
